import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Botao,
  Breadcrumb,
  Cabecalho,
  ErroLayoutContainer,
  Filtro,
  SelectUnform
} from 'src/componentes'
import {
  Tabela,
  TabelaDados,
  TabelaRef,
  TabelaResposta
} from 'src/componentes/tabela'
import { formatadores } from 'src/paginas/aluno/painel-financeiro/pagina-financeiro/formatadores'
import { Api } from 'src/servicos'
import {
  CategoriaExportacaoRelatorio,
  CategoriaExportacaoRelatorioPorExtenso,
  StatusExportacaoRelatorio,
  StatusExportacaoRelatorioPorExtenso,
  textoCategoriaExportacao,
  textoStatusExportacao,
  TipoOrdenacao
} from 'src/tipos'

import { Form, Container, Campo, Dados, CelulaAcao } from './styles'
import { DadosPesquisa } from './tipos'

const paraFiltroEnum = (valor?: string | number): string | number | undefined => {
  if (valor === undefined || valor === null || valor === '') return undefined
  return valor
}

const statusEmAndamento = (status: unknown): boolean => {
  if (
    status === StatusExportacaoRelatorio.Pendente ||
    status === StatusExportacaoRelatorio.Processando
  ) {
    return true
  }

  const nome = String(status ?? '')
  return nome === 'Pendente' || nome === 'Processando'
}

export const PaginaMeusRelatorios: React.FC = () => {
  const tabelaRef = useRef<TabelaRef>()
  const [dadosPesquisa, definirDadosPesquisa] = useState<DadosPesquisa>(null)
  const [paginas, definirPaginas] = useState<number>(null)
  const [precisaAtualizar, definirPrecisaAtualizar] = useState(false)
  const [baixandoId, definirBaixandoId] = useState<string>(null)

  const acaoPesquisa = (dados: DadosPesquisa) => {
    if (dados !== null && paginas !== 1) {
      definirPaginas(1)
    }

    definirDadosPesquisa(dados)
  }

  const obterDados = async (
    numeroPaginas: number,
    quantidadePorPagina: number,
    _chaveOrdenacao?: string,
    _ordem?: TipoOrdenacao
  ): Promise<TabelaResposta | null> => {
    if (paginas !== null) {
      numeroPaginas = paginas
    }

    const dados = await Api.ObterExportacoesRelatorio(
      {
        categoria: paraFiltroEnum(dadosPesquisa?.categoria) as
          | CategoriaExportacaoRelatorio
          | undefined,
        status: paraFiltroEnum(dadosPesquisa?.status) as
          | StatusExportacaoRelatorio
          | undefined
      },
      numeroPaginas,
      quantidadePorPagina
    )

    if (!dados) return null

    definirPaginas(null)
    definirPrecisaAtualizar(
      dados.registros.some(item => statusEmAndamento(item.status))
    )

    return {
      Dados: dados.registros,
      Paginacao: {
        Pagina:
          dados.opcoesPaginacao.totalPaginas < dados.opcoesPaginacao.pagina
            ? 1
            : dados.opcoesPaginacao.pagina,
        TotalRegistros: dados.opcoesPaginacao.totalRegistros
      }
    } as TabelaResposta
  }

  const baixarArquivo = async (linha: TabelaDados) => {
    if (!linha?.id || !linha.podeBaixar) return

    try {
      definirBaixandoId(linha.id)
      const titulo = String(linha.titulo ?? 'relatorio').replace(/\s+/g, '_')
      const arquivo = await Api.ObterArquivoExportacaoRelatorio(
        linha.id,
        `${titulo}.xlsx`
      )
      if (arquivo === false) {
        toast('Arquivo não encontrado.', { type: 'info' })
      }
    } catch {
      toast('Não foi possível baixar o arquivo.', { type: 'error' })
    } finally {
      definirBaixandoId(null)
    }
  }

  useEffect(() => {
    if (dadosPesquisa === null) return
    tabelaRef.current?.CarregarDados()
  }, [dadosPesquisa])

  useEffect(() => {
    if (!precisaAtualizar) return
    const intervalo = window.setInterval(() => {
      tabelaRef.current?.CarregarDados()
    }, 8000)
    return () => window.clearInterval(intervalo)
  }, [precisaAtualizar])

  return (
    <>
      <Cabecalho>
        <Breadcrumb
          titulo="Meus Relatórios"
          atalhos={[
            {
              texto: 'Relatórios'
            },
            {
              texto: 'Meus Relatórios'
            }
          ]}
        />
      </Cabecalho>
      <Filtro>
        <Form acaoSucesso={acaoPesquisa}>
          <Container>
            <Campo mr>
              <SelectUnform
                id="slct_categoria"
                name="categoria"
                label="Categoria"
                placeholder="Todas"
                opcoes={[
                  {
                    id: CategoriaExportacaoRelatorio[
                      CategoriaExportacaoRelatorio.Academicos
                    ],
                    texto:
                      CategoriaExportacaoRelatorioPorExtenso[
                        CategoriaExportacaoRelatorio.Academicos
                      ]
                  },
                  {
                    id: CategoriaExportacaoRelatorio[
                      CategoriaExportacaoRelatorio.Financeiros
                    ],
                    texto:
                      CategoriaExportacaoRelatorioPorExtenso[
                        CategoriaExportacaoRelatorio.Financeiros
                      ]
                  },
                  {
                    id: CategoriaExportacaoRelatorio[
                      CategoriaExportacaoRelatorio.Gerais
                    ],
                    texto:
                      CategoriaExportacaoRelatorioPorExtenso[
                        CategoriaExportacaoRelatorio.Gerais
                      ]
                  },
                  {
                    id: CategoriaExportacaoRelatorio[
                      CategoriaExportacaoRelatorio.Comercial
                    ],
                    texto:
                      CategoriaExportacaoRelatorioPorExtenso[
                        CategoriaExportacaoRelatorio.Comercial
                      ]
                  }
                ]}
              />
            </Campo>
            <Campo>
              <SelectUnform
                id="slct_status"
                name="status"
                label="Status"
                placeholder="Todos"
                opcoes={[
                  {
                    id: StatusExportacaoRelatorio[StatusExportacaoRelatorio.Pendente],
                    texto:
                      StatusExportacaoRelatorioPorExtenso[
                        StatusExportacaoRelatorio.Pendente
                      ]
                  },
                  {
                    id: StatusExportacaoRelatorio[
                      StatusExportacaoRelatorio.Processando
                    ],
                    texto:
                      StatusExportacaoRelatorioPorExtenso[
                        StatusExportacaoRelatorio.Processando
                      ]
                  },
                  {
                    id: StatusExportacaoRelatorio[StatusExportacaoRelatorio.Concluido],
                    texto:
                      StatusExportacaoRelatorioPorExtenso[
                        StatusExportacaoRelatorio.Concluido
                      ]
                  },
                  {
                    id: StatusExportacaoRelatorio[StatusExportacaoRelatorio.Falha],
                    texto:
                      StatusExportacaoRelatorioPorExtenso[
                        StatusExportacaoRelatorio.Falha
                      ]
                  },
                  {
                    id: StatusExportacaoRelatorio[StatusExportacaoRelatorio.Expirado],
                    texto:
                      StatusExportacaoRelatorioPorExtenso[
                        StatusExportacaoRelatorio.Expirado
                      ]
                  }
                ]}
              />
            </Campo>
          </Container>
          <Botao
            type="submit"
            id="btn-aplicar-filtro"
            texto="Aplicar filtro"
            tema="Link"
          />
        </Form>
      </Filtro>
      <Dados>
        <Tabela
          ref={tabelaRef}
          Colunas={[
            {
              Chave: 'titulo',
              Texto: 'Relatório',
              Alinhamento: 'left',
              RenderizarCabecalho: () => (
                <div style={{ width: 220 }}>Relatório</div>
              )
            },
            {
              Chave: 'categoria',
              Texto: 'Categoria',
              Alinhamento: 'left',
              RenderizarCabecalho: () => (
                <div style={{ width: 120 }}>Categoria</div>
              ),
              Renderizar: (linha: TabelaDados) => (
                <span>{textoCategoriaExportacao(linha.categoria)}</span>
              )
            },
            {
              Chave: 'status',
              Texto: 'Status',
              Alinhamento: 'left',
              RenderizarCabecalho: () => (
                <div style={{ width: 120 }}>Status</div>
              ),
              Renderizar: (linha: TabelaDados) => (
                <span>{textoStatusExportacao(linha.status)}</span>
              )
            },
            {
              Chave: 'criadoEm',
              Texto: 'Solicitado em',
              Alinhamento: 'left',
              RenderizarCabecalho: () => (
                <div style={{ width: 160 }}>Solicitado em</div>
              ),
              Renderizar: (linha: TabelaDados) => (
                <span>{formatadores.dataHoraLocaleString(linha.criadoEm)}</span>
              )
            },
            {
              Chave: 'acoes',
              Texto: 'Ação',
              Alinhamento: 'left',
              RenderizarCabecalho: () => (
                <div style={{ width: 80 }}>Ação</div>
              ),
              Renderizar: (linha: TabelaDados) => (
                <CelulaAcao>
                  {linha.podeBaixar ? (
                    <Botao
                      type="button"
                      id={`btn-baixar-${linha.id}`}
                      texto="Baixar"
                      tema="Link"
                      carregando={baixandoId === linha.id}
                      onClick={() => baixarArquivo(linha)}
                    />
                  ) : (
                    <span>—</span>
                  )}
                </CelulaAcao>
              )
            }
          ]}
          ObterDados={obterDados}
          ComPaginacao
        />
      </Dados>
    </>
  )
}

export default (): JSX.Element => (
  <ErroLayoutContainer.Provider>
    <PaginaMeusRelatorios />
  </ErroLayoutContainer.Provider>
)
